import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Shield, 
  HardHat, 
  FileCheck, 
  ClipboardList,
  AlertTriangle,
  CheckCircle2
} from "lucide-react";

const SAFETY_IMG = "https://images.pexels.com/photos/8159/pexels-photo.jpg?auto=compress&w=1200";

const safetyProtocols = [
  "Job site safety protocols",
  "Field safety procedures",
  "Ongoing crew training",
  "Compliance with OSHA standards",
  "Proper equipment handling & procedures",
  "Personal protective equipment requirements"
];

const documentation = [
  { title: "Splice Documentation", description: "Comprehensive splice logs and records" },
  { title: "OTDR Test Reports", description: "Detailed optical testing documentation" },
  { title: "As-Built Documentation", description: "Complete as-built documentation support" },
  { title: "Project Reporting", description: "Reports as required by carrier standards" }
];

export default function Safety() {
  return (
    <div data-testid="safety-page">
      {/* Hero Section */}
      <section 
        className="relative py-20 md:py-32 bg-[#0F172A]"
        data-testid="safety-hero"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
                Safety & Compliance
              </span>
              <h1 className="font-heading text-5xl md:text-6xl font-black tracking-tight text-white mt-4 mb-6">
                Safety First, Always
              </h1>
              <p className="text-xl text-slate-400 leading-relaxed mb-8">
                Safety is a core operational priority at C&F Communications. We maintain 
                rigorous safety standards and comprehensive documentation practices.
              </p>
              <div className="flex items-center gap-4 p-4 bg-[#F59E0B]/20 border border-[#F59E0B]/40">
                <AlertTriangle className="h-8 w-8 text-[#F59E0B]" />
                <span className="text-[#F59E0B] font-semibold">
                  Zero Compromise on Safety Standards
                </span>
              </div>
            </div>
            <div className="relative">
              <img
                src={SAFETY_IMG}
                alt="Lineman with safety vest and hard hat"
                className="w-full h-auto object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#00B4D8] p-6 text-white">
                <Shield className="h-8 w-8 mb-2" />
                <div className="text-sm uppercase tracking-wider font-bold">Safety First</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Safety Protocols */}
      <section className="py-20 md:py-32 bg-white" data-testid="safety-protocols">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-[#00B4D8] flex items-center justify-center">
                  <HardHat className="h-6 w-6 text-white" />
                </div>
                <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
                  Our Commitment
                </span>
              </div>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6">
                Safety Protocols
              </h2>
              <div className="section-line mb-8"></div>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Every project starts with safety. Our comprehensive safety program ensures 
                that all crew members are trained, equipped, and prepared to work safely 
                in any environment.
              </p>
            </div>
            <div className="bg-slate-50 p-8 border-l-4 border-[#00B4D8]">
              <h3 className="font-heading text-xl font-bold text-slate-900 mb-6">
                Safety Standards
              </h3>
              <ul className="space-y-4">
                {safetyProtocols.map((item, index) => (
                  <li key={index} className="flex items-center gap-4" data-testid={`safety-item-${index}`}>
                    <CheckCircle2 className="h-5 w-5 text-[#00B4D8] flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Documentation & Reporting */}
      <section className="py-20 md:py-32 bg-slate-50 grid-pattern" data-testid="documentation-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-12 h-12 bg-[#00B4D8] flex items-center justify-center">
                <FileCheck className="h-6 w-6 text-white" />
              </div>
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6">
              Documentation & Reporting
            </h2>
            <div className="section-line mx-auto mb-6"></div>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We maintain detailed documentation throughout every project, ensuring 
              complete transparency and compliance with carrier requirements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {documentation.map((doc, index) => (
              <div 
                key={index}
                className="bg-white border border-slate-200 p-8 hover:border-[#00B4D8] transition-colors"
                data-testid={`doc-item-${index}`}
              >
                <div className="w-12 h-12 bg-[#0F172A] flex items-center justify-center mb-6">
                  <ClipboardList className="h-6 w-6 text-[#00B4D8]" />
                </div>
                <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">
                  {doc.title}
                </h3>
                <p className="text-slate-600 text-sm">
                  {doc.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#00B4D8]" data-testid="safety-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Questions About Our Safety Program?
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Contact us to learn more about our safety standards and compliance procedures.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center bg-white text-[#0F172A] px-8 py-4 text-sm font-bold uppercase tracking-wider transition-all duration-300 shadow-[4px_4px_0px_0px_rgba(15,23,42,0.3)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(15,23,42,0.3)]"
            data-testid="safety-cta-button"
          >
            Contact Our Team
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
